import messages from '../intl/index.js';
import { Dropdown } from './Dropdown.js';
import dropdownStyles from './Dropdown.module.scss.js';
import { SelectAllItem, selectAllValue } from './SelectAllItem.js';
import { TreeItem } from './TreeItem.js';
import { flatten, areAllSelected, reducer, visit, find } from './treeLogic.js';
import { defaultSearchFn, useSelectDropdown, defaultMultiSummary } from './util.js';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { useMessageFormatter } from '../../i18n/format.js';

/**
 * A dropdown input with filterable options.
 */
function TreeSelectInput(props) {
  // Resources for developing custom combo boxes:
  // https://www.w3.org/WAI/ARIA/apg/patterns/combobox/
  // https://www.24a11y.com/2019/select-your-poison/
  const {
    className: callerClassName,
    disabled,
    id,
    loadingState,
    name,
    options,
    placeholder,
    renderSummary,
    searchFn = defaultSearchFn,
    tag,
    value,
    variant = 'default',
    onBlur,
    onChange,
    onFocus,
    onOpen,
    onSearch,
    onToggle,
    changeVariant = 'multi'
  } = props;
  const flatOptions = flatten(options);
  const {
    formatMessage
  } = useMessageFormatter(messages);
  const {
    activeOption,
    activeValue,
    closePopover,
    filteredOptions,
    open,
    search,
    searchRef,
    setActiveValue,
    uid,
    onDropdownClick,
    onSearchInput,
    onSearchKeyDown
  } = useSelectDropdown({
    ...props,
    extraValues: [selectAllValue],
    filterOptions: option => option.visible,
    options: flatOptions,
    searchFn: treeSearchFn,
    onOpen: handleOpen,
    onOptionSelect: handleOptionSelect,
    onActiveOptionCollapse: handleActiveOptionCollapse,
    onActiveOptionExpand: handleActiveOptionExpand
  });
  function treeSearchFn(options, search) {
    const matches = searchFn(options, search);
    const matchedValues = new Set(matches.map(option => option.value));

    // Include ancestors of matches in the result set
    for (const option of matches) {
      for (let parent = option.parent; parent != null; parent = parent.parent) {
        matchedValues.add(parent.value);
      }
    }

    // Include children of matches in the result set
    visit(matches, option => matchedValues.add(option.value));
    return options.filter(option => matchedValues.has(option.value));
  }
  function handleOpen() {
    const event = {
      type: 'open',
      value: ''
    };
    const next = reducer({
      options,
      value
    }, event);
    onOpen?.({
      ...event,
      next
    });
  }
  function handleOptionSelect(value) {
    if (value === selectAllValue) {
      handleSelectAll();
    } else {
      handleSelectOne(value);
    }
  }
  function handleSelectAll() {
    const event = {
      type: 'select-all'
    };
    const next = reducer({
      options,
      value
    }, event);
    onChange(next.value, {
      ...event,
      next
    });
    if (changeVariant === 'single' || changeVariant === 'leaf') {
      closePopover();
    }
  }
  function handleSelectOne(optionValue) {
    const type = value != null && value.includes(optionValue) ? changeVariant === 'multi' ? 'remove' : 'remove-single' : changeVariant === 'multi' ? 'add' : 'add-single';
    const event = {
      type,
      value: optionValue
    };
    const next = reducer({
      options,
      value
    }, event);
    onChange(next.value, {
      ...event,
      next
    });
    if (changeVariant === 'single' || changeVariant === 'leaf') {
      closePopover();
    }
  }
  function handleActiveOptionCollapse() {
    if (activeOption == null) {
      return;
    }
    if (activeOption.children?.length && activeOption.expanded) {
      const event = {
        type: 'toggle',
        value: activeOption.value
      };
      const next = reducer({
        options,
        value
      }, event);
      onToggle(activeOption.value, {
        ...event,
        next
      });
    } else if (activeOption.parent) {
      setActiveValue(activeOption.parent.value);
    }
  }
  function handleActiveOptionExpand() {
    if (activeOption == null) {
      return;
    }
    if (!activeOption.children?.length) {
      return;
    }
    if (!activeOption.expanded) {
      const event = {
        type: 'toggle',
        value: activeOption.value
      };
      const next = reducer({
        options,
        value
      }, event);
      onToggle(activeOption.value, {
        ...event,
        next
      });
    } else {
      setActiveValue(activeOption.children[0].value);
    }
  }
  function handleOptionExpandToggle(optionValue) {
    const event = {
      type: 'toggle',
      value: optionValue
    };
    const next = reducer({
      options,
      value
    }, event);
    onToggle(optionValue, {
      ...event,
      next
    });
  }
  const selectedOptions = flatOptions.filter(option => value?.includes(option.value));
  const selectedLabel = renderSummary?.(value) ?? defaultMultiSummary(selectedOptions, formatMessage);
  const activeLabelOrPlaceholder = activeOption != null ? renderSummary?.([activeValue]) ?? activeOption?.label : placeholder;
  const activeId = activeValue === selectAllValue ? `${uid}-option--select-all` : activeValue != null ? `${uid}-option-${activeValue}` : undefined;
  return /*#__PURE__*/jsx(Dropdown, {
    activeId: activeId,
    className: callerClassName,
    closePopover: closePopover,
    disabled: disabled,
    id: id,
    listId: `${uid}-list`,
    loadingState: loadingState,
    name: name,
    open: open,
    placeholder: activeLabelOrPlaceholder,
    search: search,
    searchRef: searchRef,
    selectedLabel: selectedLabel,
    tag: tag,
    tagKey: "data-dsx--tree-select-input",
    value: JSON.stringify(value),
    variant: variant,
    onBlur: onBlur,
    onDropdownClick: onDropdownClick,
    onFocus: onFocus,
    onSearch: onSearch,
    onSearchInput: onSearchInput,
    onSearchKeyDown: onSearchKeyDown,
    children: filteredOptions.length > 0 && /*#__PURE__*/jsxs(Fragment, {
      children: [changeVariant === 'multi' && /*#__PURE__*/jsxs(Fragment, {
        children: [/*#__PURE__*/jsx(SelectAllItem, {
          activeValue: activeValue,
          selected: areAllSelected(options, value),
          id: `${uid}-option--select-all`,
          onSelect: handleSelectAll
        }), /*#__PURE__*/jsx("div", {
          className: dropdownStyles['divider']
        })]
      }), filteredOptions.map(option => /*#__PURE__*/jsx(TreeItem, {
        activeValue: activeValue,
        option: option,
        id: `${uid}-option-${option.value}`,
        value: value,
        onExpandToggle: handleOptionExpandToggle,
        onSelect: handleOptionSelect,
        selectVariant: changeVariant
      }, option.value))]
    })
  });
}
TreeSelectInput.find = find;
TreeSelectInput.visit = visit;

export { TreeSelectInput };
